/* eslint-disable object-curly-newline */
import {
  PrivateRoute,
  withDocumentTitle,
  withSuspense,
  withModuleAccess,
} from '@panna/auth';
import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';
import MainLayout from 'src/layouts/MainLayout';
import JobRequisitionLayout from 'src/layouts/JobRequisitionLayout';
import CandidateAppliedJobs from 'src/pages/CandidateAppliedJobs';
import CandidateDocuments from 'src/pages/CandidateDocuments';
import CandidateInfo from 'src/pages/CandidateInfo';
import CandidateResume from 'src/pages/CandidateResume';
import ConsentForm from 'src/pages/ConsentForm';
import AuthorizationForm from 'src/pages/AuthorizationForm';
import { Module } from 'src/access';
import { CandidateAssessments, JobTerms, VendorManagement } from 'src/pages';
import { useSelector } from 'react-redux';
import InterviewTabs from './InterviewsTabs';
import SettingsTabs from './SettingTabs';
import DiceTabs from './DiceTabs';

const JobRequisition = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/JobRequisition'))),
  'Job Requisition',
);

const ATSDashboardPage = withDocumentTitle(
  withModuleAccess(
    withSuspense(React.lazy(() => import('src/pages/ATSDashboard'))),
    Module.ATS_DASHBOARD,
  ),
  'ATS Dashboard',
);

const DiceDashboard = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/DiceDashboard'))),
  'Dice Dashboard',
);

const DiceSearch = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/DiceSearch'))),
  'Dice Search',
);

const DiceProfilesHistory = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/DiceProfilesHistory'))),
  'Dice Profiles History',
);

const TalentPool = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/TalentPool'))),
  'Talent Pool',
);

const ViewJob: any = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/ViewJob'))),
  'Job Requisition',
);

const CurrentInterviews = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/CurrentInterviews'))),
  'Current Interviews',
);

const CompletedInterviews = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/CompletedInterviews'))),
  'Completed Interviews',
);

const InterviewSlots = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/InterviewSlots'))),
  'Interview Slots',
);

const ProfileLayout = withSuspense(
  React.lazy(() => import('src/layouts/ProfileLayout')),
);

const Careers = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/Careers'))),
  'Careers',
);

const JobDetails = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/JobDetail'))),
  'Job Details',
);

const Locations = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/Locations'))),
  'Locations',
);

const Skills = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/Skills'))),
  'Skills',
);

const CandidateJobTitles = withDocumentTitle(
  withSuspense(React.lazy(() => import('src/pages/CandidateJobTitles'))),
  'Candidate Job Titles',
);

function CandidateProfileComponent() {
  const { candidateId } = useParams();
  const candidateInfo = useSelector(
    (state: any) => state?.selectedTalentPoolProfile?.candidateInfo,
  );

  useEffect(() => {
    async function fetchCandidateData() {
      document.title = `${candidateInfo?.firstName} ${candidateInfo?.lastName}`;
    }

    fetchCandidateData();
  }, [candidateId]);

  return <ProfileLayout />;
}

const CandidateProfile = withDocumentTitle(
  CandidateProfileComponent,
  'Candidate Profile',
);

function RootRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PrivateRoute component={<Outlet />} />}>
          <Route path="/" element={<MainLayout />}>
            <Route path="/dashboard" element={<ATSDashboardPage />} />
            <Route
              index
              element={<Navigate to="/job-requisition" replace />}
            />
            <Route path="/job-requisition" element={<JobRequisitionLayout />}>
              <Route path="job-list" Component={JobRequisition} />
              <Route path="view-job/:id" Component={ViewJob} />
            </Route>
            <Route path="/interviews" Component={InterviewTabs}>
              <Route
                index
                element={<Navigate to="/interviews/current" replace />}
              />
              <Route path="current" Component={CurrentInterviews} />
              <Route path="completed" Component={CompletedInterviews} />
              <Route path="slots" Component={InterviewSlots} />
            </Route>
            <Route index path="/talent-pool" Component={TalentPool} />
            {/* <Route index path="/dice" Component={Dice} /> */}
            <Route path="dice" element={<DiceTabs />}>
              <Route index path="search" Component={DiceSearch} />
              <Route index path="dashboard" Component={DiceDashboard} />
              <Route index path="history" Component={DiceProfilesHistory} />
            </Route>
            <Route path="*" element={<Navigate to="/job-requisition" replace />} />
            <Route path="candidate-profile/:candidateId" element={<CandidateProfile />}>
              <Route
                index
                element={<Navigate to="info" replace />}
              />
              <Route path="resume" Component={CandidateResume} />
              <Route path="history" Component={CandidateAppliedJobs} />
              <Route path="info" Component={CandidateInfo} />
              <Route path="docs" Component={CandidateDocuments} />
              <Route path="assessments" Component={CandidateAssessments} />
            </Route>
            <Route index path="/vendors" Component={VendorManagement} />
            <Route path="settings" Component={SettingsTabs}>
              <Route
                index
                element={<Navigate to="/settings/locations" replace />}
              />
              <Route path="locations" Component={Locations} />
              <Route path="skills" Component={Skills} />
              <Route path="candidate-job-titles" Component={CandidateJobTitles} />
            </Route>
          </Route>
        </Route>
        <Route index path="/consent/:encryptedCandidateId" Component={ConsentForm} />
        <Route index path="/authorization/:encryptedCandidateId" Component={AuthorizationForm} />
        <Route index path="/:orgId/careers" Component={Careers} />
        <Route index path="/:orgId/job-details/:jobSlug" Component={JobDetails} />
        <Route index path="/:orgId/job-terms" Component={JobTerms} />
      </Routes>
    </BrowserRouter>
  );
}

export default RootRouter;
